import { type ReactNode, useCallback, useState } from 'react';
import { Body } from 'components/text';
import MuiAccordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { Chip } from '@mui/material';

import ExpandButton from 'blocks/CareerListSection/components/expand-button';

export type AccordionListProps<T> = {
  items: T[];
  getId: (item: T) => string;
  getTitle: (item: T) => string;
  getChipLabel?: (item: T) => string;
  getContent: (item: T) => string | T[];
  renderContent?: (item: T) => ReactNode;
  withLabel?: boolean;
};

const AccordionList = <T,>({
  items,
  renderContent,
  getId,
  getTitle,
  getContent,
  getChipLabel = () => '',
  withLabel = false,
}: AccordionListProps<T>) => {
  const [expandedPanelId, setExpandedPanelId] = useState<string | null>(null);

  const handleAccordionChange = useCallback(
    (id: string) => () => {
      setExpandedPanelId(prevId => (prevId === id ? null : id));
    },
    []
  );

  return (
    <div className="accordion-list-container">
      <div className="accordion-list">
        {items.map(item => {
          const id = getId(item);
          const title = getTitle(item);
          const content = getContent(item);
          const showText = typeof content === 'string' && content.length > 0;
          const chipLabel = getChipLabel(item);
          return (
            <MuiAccordion
              key={id}
              disableGutters
              elevation={0}
              className="accordion-accordion"
              expanded={expandedPanelId === id}
              onChange={handleAccordionChange(id)}
            >
              <AccordionSummary
                aria-controls={id}
                id={id}
                className='accordion-accordion__summary'
                expandIcon={
                  <ExpandButton
                    expanded={expandedPanelId === id}
                    withLabel={withLabel}
                  />
                }
              >
                <Body
                  fontSize={18}
                  color="gold-dark"
                  className="accordion-accordion__title"
                >
                  {title}
                </Body>
                {chipLabel && (
                  <Chip label={chipLabel} size="small" className="role-tag" />
                )}
              </AccordionSummary>
              <AccordionDetails>
                <div className="accordion-container">
                  <div className="accordion-container__left">
                    {!showText && renderContent?.(item)}
                    {showText && <Body color="grey-dark">{content}</Body>}
                  </div>
                </div>
              </AccordionDetails>
            </MuiAccordion>
          );
        })}
      </div>
    </div>
  );
};

export default AccordionList;
